// ===== Production-Ready WebRTC Chat Interface =====

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { 
  Phone, 
  VideoCall, 
  Send, 
  AttachFile, 
  EmojiEmotions,
  MoreVert,
  ArrowBack,
  CallEnd,
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  VolumeUp,
  VolumeOff,
  ScreenShare,
  StopScreenShare,
  Error as ErrorIcon,
  Refresh,
  Warning
} from '@mui/icons-material';
import { 
  IconButton, 
  Snackbar, 
  Alert, 
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';

// Production-ready imports
import { WebRTCManager } from './managers/WebRTCManager';
import { SocketManager } from './managers/SocketManager';
import { MessageManager } from './managers/MessageManager';
import CallInterface from './components/CallInterface';
import MessageInput from './components/MessageInput';
import MessageList from './components/MessageList';
import IncomingCallDialog from './components/IncomingCallDialog';
import ErrorBoundary from './components/ErrorBoundary';
import { CONFIG } from './config/ChatConfig';
import { CALL_STATES, MESSAGE_TYPES, ERROR_TYPES } from './constants/ChatConstants';
import { validateMessage, sanitizeInput } from './utils/ValidationUtils';
import { Logger } from './utils/Logger';
import { PerformanceMonitor } from './utils/PerformanceMonitor';
import api from '../../api/apiInstance';

const ChatAndCall = ({ currentUser, receiver, onClose, socket, incomingCallData, onMessageSent }) => {
  // Core State Management
  const [messages, setMessages] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  // Connection State
  const [isConnected, setIsConnected] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // Call State
  const [isInCall, setIsInCall] = useState(false);
  const [callType, setCallType] = useState(null);
  const [callStatus, setCallStatus] = useState(CALL_STATES.IDLE);
  const [callError, setCallError] = useState('');
  const [incomingCall, setIncomingCall] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  
  // Media Controls State
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  
  // UI State
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [criticalError, setCriticalError] = useState(null);

  // Refs for managers and DOM elements
  const webRTCManagerRef = useRef(null);
  const socketManagerRef = useRef(null);
  const messageManagerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const processedMessageIds = useRef(new Set());
  const mountedRef = useRef(true);
  const performanceMonitorRef = useRef(null);
  const handleEndCallRef = useRef(null);
  // Buffers ICE candidates that arrive before WebRTCManager is initialized (callee side race condition)
  const earlyIceCandidatesRef = useRef([]);

  // Production utilities
  const logger = useMemo(() => new Logger('ChatAndCall'), []);
  
  // STUN/TURN server configuration state
  const [webRTCConfig, setWebRTCConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  // Becomes true once WebRTCManager + SocketManager are both created
  const [managersInitialized, setManagersInitialized] = useState(false);
  
  // Enhanced validation with detailed error reporting
  const validationError = useMemo(() => {
    if (!currentUser) return 'Current user is required';
    if (!currentUser.socketId) return 'Current user socket ID is required';
    if (!receiver) return 'Receiver information is required';
    if (!receiver.socketId && !receiver._id && !receiver.id) return 'Receiver ID is required';
    return null;
  }, [currentUser, receiver]);

  // Memoized receiver ID resolution with fallback logic
  const receiverUserId = useMemo(() => {
    if (!receiver) return null;
    
    return receiver._id || 
           receiver.userId || 
           receiver.id || 
           receiver.doctorId || 
           receiver.patientId || 
           receiver.user_id || 
           receiver.user?._id || 
           receiver.user?.id || 
           receiver.doctor?._id || 
           receiver.patient?._id || 
           receiver.receiverId ||
           // Secure fallback for testing (remove in production)
           (process.env.NODE_ENV === 'development' && currentUser?.socketId === '67deb6b8955d923bb6af4812' ? 
            '67deb746955d923bb6af4831' : '67deb6b8955d923bb6af4812');
  }, [receiver, currentUser?.socketId]);

  // Chat room ID with proper validation and security
  const chatRoomId = useMemo(() => {
    if (receiver?.appointmentId) return `appointment-${receiver.appointmentId}`;
    if (currentUser?.socketId && receiverUserId) {
      const sortedIds = [currentUser.socketId, receiverUserId].sort();
      return `chat-${sortedIds[0]}-${sortedIds[1]}`;
    }
    return 'default-room';
  }, [currentUser?.socketId, receiverUserId, receiver?.appointmentId]);

  // Production-ready notification system
  const addNotification = useCallback((message, type = 'info', duration = CONFIG.UI.NOTIFICATION_DURATION) => {
    if (!mountedRef.current) return;
    
    const id = Date.now() + Math.random();
    const notification = { id, message, type, duration };
    
    setNotifications(prev => {
      // Limit notifications to prevent memory issues
      const newNotifications = [...prev, notification];
      return newNotifications.slice(-CONFIG.UI.MAX_NOTIFICATIONS);
    });
    
    // Auto-remove notification
    setTimeout(() => {
      if (mountedRef.current) {
        setNotifications(prev => prev.filter(n => n.id !== id));
      }
    }, duration);
    
    // Log notifications for monitoring
    logger.info(`Notification: ${type} - ${message}`);
  }, [logger]);

  // Critical error handler
  const handleCriticalError = useCallback((error, context) => {
    logger.error(`Critical error in ${context}:`, error);
    setCriticalError({ error, context });
    setShowErrorDialog(true);
    
    // Report to monitoring service in production
    if (process.env.NODE_ENV === 'production' && window.errorReporting) {
      window.errorReporting.captureException(error, { context });
    }
  }, [logger]);

  // Fetch STUN/TURN credentials from backend
  const fetchWebRTCCredentials = useCallback(async () => {
    try {
      setConfigLoading(true);
      logger.info('🌐 Fetching STUN/TURN credentials from backend...');
      
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
      const response = await api.get(`${apiBaseUrl}api/v1/webrtc/turn-credentials`, {
        headers: { isPublic: true }
      });

      if (response.data && response.data.data.iceServers) {
        const config = {
          iceServers: response.data.data.iceServers,
          iceCandidatePoolSize: parseInt(process.env.REACT_APP_ICE_CANDIDATE_POOL_SIZE) || 10,
          iceTransportPolicy: process.env.REACT_APP_ICE_TRANSPORT_POLICY || 'all',
          bundlePolicy: process.env.REACT_APP_BUNDLE_POLICY || 'max-bundle',
          rtcpMuxPolicy: process.env.REACT_APP_RTCP_MUX_POLICY || 'require'
        };
        
        setWebRTCConfig(config);
        // Fix TURN server detection - handle both string and array formats
        const hasSTUN = config.iceServers.some(server => {
          const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
          return urls.some(url => url.startsWith('stun:'));
        });
        
        const hasTURN = config.iceServers.some(server => {
          const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
          return urls.some(url => url.startsWith('turn:'));
        });
        
        logger.info('✅ WebRTC credentials fetched successfully:', {
          serverCount: config.iceServers.length,
          hasSTUN,
          hasTURN
        });
        
        //addNotification('WebRTC servers configured successfully', 'success');
      } else {
        throw new Error('Invalid response format from WebRTC credentials endpoint');
      }
    } catch (error) {
      logger.error('❌ Failed to fetch WebRTC credentials:', error);
      
      // Fallback to environment variable STUN servers
      const fallbackConfig = {
        iceServers: [
          { urls: process.env.REACT_APP_STUN_SERVER_1 || 'stun:stun.l.google.com:19302' },
          { urls: process.env.REACT_APP_STUN_SERVER_2 || 'stun:stun1.l.google.com:19302' },
          { urls: process.env.REACT_APP_STUN_SERVER_3 || 'stun:stun2.l.google.com:19302' }
        ].filter(server => server.urls),
        iceCandidatePoolSize: parseInt(process.env.REACT_APP_ICE_CANDIDATE_POOL_SIZE) || 10,
        iceTransportPolicy: process.env.REACT_APP_ICE_TRANSPORT_POLICY || 'all',
        bundlePolicy: process.env.REACT_APP_BUNDLE_POLICY || 'max-bundle',
        rtcpMuxPolicy: process.env.REACT_APP_RTCP_MUX_POLICY || 'require'
      };
      
      setWebRTCConfig(fallbackConfig);
      logger.warn('🔄 Using fallback STUN servers:', fallbackConfig);
      //addNotification('Using fallback STUN servers (no TURN available)', 'warning');
    } finally {
      setConfigLoading(false);
    }
  }, [logger, addNotification]);

  // Fetch WebRTC credentials on component mount
  useEffect(() => {
    fetchWebRTCCredentials();
  }, [fetchWebRTCCredentials]);

  // Clear messages and request history when receiver changes
  useEffect(() => {
    if (receiverUserId) {
      logger.info('Receiver changed, clearing messages and requesting history', { 
        receiverUserId, 
        currentUserId: currentUser?.socketId 
      });
      setMessages([]);
      processedMessageIds.current.clear();
      
      // Request chat history for the new receiver if socket manager is available
      if (socketManagerRef.current && currentUser?.socketId) {
        logger.info('Requesting chat history via SocketManager');
        setTimeout(() => {
          const requested = socketManagerRef.current.requestChatHistory(currentUser.socketId, receiverUserId);
          logger.info('Chat history request result:', { requested });
        }, 1000); // Increased delay to ensure socket manager is properly initialized
      } else {
        logger.warn('Cannot request chat history - missing socket manager or current user');
      }
    }
  }, [receiverUserId, currentUser?.socketId, logger]);

  // Direct socket listener for real-time messages (Critical Fix)
  useEffect(() => {
    if (!socket || !currentUser?.socketId) return;

    const handleDirectMessage = (message) => {
      logger.info('🔥 DIRECT MESSAGE RECEIVED FROM SOCKET:', message);
      
      // Only accept messages intended for current user
      if (message.receiver !== currentUser.socketId) {
        logger.warn('Message not for current user, ignoring:', { 
          messageReceiver: message.receiver, 
          currentUserId: currentUser.socketId 
        });
        return;
      }

      // Critical Fix: Only show messages between current user and selected receiver
      const isConversationMessage = (
        (message.sender === currentUser?.socketId && message.receiver === receiverUserId) ||
        (message.sender === receiverUserId && message.receiver === currentUser?.socketId)
      );
      
      if (!isConversationMessage) {
        logger.info(`🚫 Real-time message ${message.id} filtered out - not part of current conversation:`, {
          sender: message.sender,
          receiver: message.receiver,
          currentUserId: currentUser?.socketId,
          receiverUserId: receiverUserId
        });
        return;
      }

      // Add message directly to chat
      if (!processedMessageIds.current.has(message.id)) {
        processedMessageIds.current.add(message.id);
        setMessages(prev => {
          const newMessages = [...prev, {
            ...message,
            timestamp: new Date(message.timestamp)
          }];
          logger.info('✅ Message added to chat:', message.text);
          return newMessages.slice(-CONFIG.MESSAGE.HISTORY_LIMIT);
        });
      }
    };

    socket.on('receive-direct-message', handleDirectMessage);
    logger.info('🔥 Direct socket listener attached for user:', currentUser.socketId);

    return () => {
      socket.off('receive-direct-message', handleDirectMessage);
      logger.info('🔥 Direct socket listener removed');
    };
  }, [socket, currentUser?.socketId, logger]);

  // Early ICE candidate buffer: when this component is the callee (incomingCallData present),
  // ICE candidates from the remote peer may arrive before WebRTCManager is initialized because
  // fetchWebRTCCredentials() is async. Capture them here and replay once the manager is ready.
  useEffect(() => {
    if (!socket || !incomingCallData) return;

    const handleEarlyIce = (data) => {
      if (!webRTCManagerRef.current) {
        earlyIceCandidatesRef.current.push(data);
        logger.info('Buffered early ICE candidate, total:', earlyIceCandidatesRef.current.length);
      }
    };

    socket.on('webrtc-ice-candidate', handleEarlyIce);
    return () => {
      socket.off('webrtc-ice-candidate', handleEarlyIce);
    };
  }, [socket, incomingCallData, logger]);

  // Initialize managers with proper error handling
  useEffect(() => {
    if (validationError) {
      logger.error('Validation failed:', validationError);
      addNotification(validationError, 'error');
      return;
    }

    // Wait for WebRTC config to be loaded
    if (configLoading || !webRTCConfig) {
      logger.info('Waiting for WebRTC configuration to load...');
      return;
    }

    let cleanup = () => {};

    try {
      // Initialize Performance Monitor
      performanceMonitorRef.current = new PerformanceMonitor('ChatAndCall');
      performanceMonitorRef.current.start();

      // Initialize WebRTC Manager with fetched configuration
      webRTCManagerRef.current = new WebRTCManager({
        logger,
        config: {
          ...CONFIG.WEBRTC,
          iceServers: webRTCConfig.iceServers,
          iceCandidatePoolSize: webRTCConfig.iceCandidatePoolSize,
          iceTransportPolicy: webRTCConfig.iceTransportPolicy,
          bundlePolicy: webRTCConfig.bundlePolicy,
          rtcpMuxPolicy: webRTCConfig.rtcpMuxPolicy
        },
        onCallStateChange: (state) => {
          if (!mountedRef.current) return;
          setCallStatus(state);
          logger.info('Call state changed:', state);
        },
        onError: (error) => {
          if (!mountedRef.current) return;
          setCallError(error.message);
          addNotification(error.message, 'error');
        },
        onMediaReady: (localStream, remoteStream) => {
          if (!mountedRef.current) return;
          
          if (localVideoRef.current && localStream) {
            localVideoRef.current.srcObject = localStream;
          }
          if (remoteVideoRef.current && remoteStream) {
            remoteVideoRef.current.srcObject = remoteStream;
          }
        },
        onIncomingCall: (callData) => {
          if (!mountedRef.current) return;
          setIncomingCall(callData);
          addNotification(`Incoming ${callData.callType} call from ${callData.callerName}`, 'info');
        },
        onIceCandidate: (candidateData) => {
          if (!mountedRef.current) return;
          logger.debug('Sending ICE candidate:', candidateData);
          // Send ICE candidate via socket
          socketManagerRef.current?.sendIceCandidate({
            ...candidateData,
            receiverId: receiverUserId
          });
        },
        onCallAnswer: (answerData) => {
          if (!mountedRef.current) return;
          logger.info('Sending call answer:', answerData);
          // Send call answer via socket
          socketManagerRef.current?.sendCallAnswer({
            ...answerData,
            callerId: receiverUserId
          });
        }
      });

      // Initialize Socket Manager with existing socket (don't auto-connect)
      socketManagerRef.current = new SocketManager({
        socket: socket,
        currentUser,
        receiverUserId,
        chatRoomId,
        logger,
        config: CONFIG.SOCKET,
        autoConnect: false, // Use existing socket connection from App.js
        onConnectionChange: (connected) => {
          if (!mountedRef.current) return;
          setIsConnected(connected);
          if (connected) {
            setConnectionError(null);
            setRetryCount(0);
          }
        },
        onUserStatusChange: (online) => {
          if (!mountedRef.current) return;
          setIsOnline(online);
        },
        onError: (error) => {
          if (!mountedRef.current) return;
          setConnectionError(error.message);
          addNotification(error.message, 'error');
        },
        onMessageReceived: (message) => {
          if (!mountedRef.current) return;
          
          // Critical Fix: Only show messages between current user and selected receiver
          const isConversationMessage = (
            (message.sender === currentUser?.socketId && message.receiver === receiverUserId) ||
            (message.sender === receiverUserId && message.receiver === currentUser?.socketId)
          );
          
          if (!isConversationMessage) {
            logger.info(`🚫 SocketManager message ${message.id} filtered out - not part of current conversation:`, {
              sender: message.sender,
              receiver: message.receiver,
              currentUserId: currentUser?.socketId,
              receiverUserId: receiverUserId
            });
            return;
          }
          
          // Only validate text for text messages, allow other types
          if (message.type === 'text' && !validateMessage(message.text)) {
            logger.warn("Invalid message received:", message);
            return;
          }
          
          if (!processedMessageIds.current.has(message.id)) {
            processedMessageIds.current.add(message.id);
            setMessages(prev => {
              const processedMessage = {
                ...message,
                timestamp: new Date(message.timestamp)
              };
              
              // Only sanitize text for text messages
              if (message.type === 'text') {
                processedMessage.text = sanitizeInput(message.text);
              }
              
              const newMessages = [...prev, processedMessage];
              return newMessages.slice(-CONFIG.MESSAGE.HISTORY_LIMIT);
            });
          }
        },
        onMessageHistory: (messages) => {
          if (!mountedRef.current) return;
          logger.info('🔄 Processing message history', { 
            messageCount: messages?.length,
            currentUserId: currentUser?.socketId,
            receiverUserId: receiverUserId
          });
          
          if (!messages || messages.length === 0) {
            logger.info('📭 No messages in history');
            setMessages([]);
            return;
          }
          
          // Debug each message before processing
          messages.forEach((msg, index) => {
            logger.info(`📨 Raw message ${index}:`, {
              id: msg.id,
              sender: msg.sender,
              receiver: msg.receiver,
              text: msg.text,
              type: msg.type,
              timestamp: msg.timestamp
            });
          });
          
          const validMessages = messages
            .filter(msg => {
              if (!msg) {
                logger.warn('❌ Null message in history');
                return false;
              }
              if (!msg.id) {
                logger.warn('❌ Message without ID in history', msg);
                return false;
              }
              
              // Critical Fix: Only show messages between current user and selected receiver
              const isConversationMessage = (
                (msg.sender === currentUser?.socketId && msg.receiver === receiverUserId) ||
                (msg.sender === receiverUserId && msg.receiver === currentUser?.socketId)
              );
              
              if (!isConversationMessage) {
                logger.info(`🚫 Message ${msg.id} filtered out - not part of current conversation:`, {
                  sender: msg.sender,
                  receiver: msg.receiver,
                  currentUserId: currentUser?.socketId,
                  receiverUserId: receiverUserId
                });
                return false;
              }
              
              logger.info(`✅ Message ${msg.id} passed validation:`, {
                sender: msg.sender,
                receiver: msg.receiver,
                text: msg.text,
                isFromCurrentUser: msg.sender === currentUser?.socketId,
                isToCurrentUser: msg.receiver === currentUser?.socketId
              });
              
              return true;
            })
            .map(msg => {
              const processedMessage = {
                ...msg,
                timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
                type: msg.type || 'text'
              };
              
              // Only sanitize text for text messages
              if (msg.type === 'text' || !msg.type) {
                processedMessage.text = msg.text || '';
              }
              
              logger.info(`🔧 Processed message ${msg.id}:`, {
                id: processedMessage.id,
                sender: processedMessage.sender,
                receiver: processedMessage.receiver,
                text: processedMessage.text,
                timestamp: processedMessage.timestamp
              });
              
              return processedMessage;
            })
            .slice(-CONFIG.MESSAGE.HISTORY_LIMIT);
            
          logger.info('📝 Final processed message history', { 
            validCount: validMessages.length,
            messageIds: validMessages.map(m => m.id),
            senders: validMessages.map(m => m.sender),
            receivers: validMessages.map(m => m.receiver)
          });
          
          validMessages.forEach(msg => processedMessageIds.current.add(msg.id));
          setMessages(validMessages);
          
          logger.info('🎯 Messages set in state, total messages:', validMessages.length);
        },
        onIncomingCall: (callData) => {
          if (!mountedRef.current) return;
          logger.info('Incoming call received via SocketManager:', callData);
          setIncomingCall(callData);
          addNotification(`Incoming ${callData.callType} call from ${callData.callerName}`, 'info');
        },
        onCallAnswer: (data) => {
          if (!mountedRef.current) return;
          logger.info('Call answer received via SocketManager:', data);
          // Forward to WebRTC manager
          webRTCManagerRef.current?.handleCallAnswer(data);
        },
        onIceCandidate: (data) => {
          if (!mountedRef.current) return;
          logger.debug('ICE candidate received via SocketManager:', data);
          // Forward to WebRTC manager
          webRTCManagerRef.current?.handleIceCandidate(data);
        },
        onCallEnded: (data) => {
          if (!mountedRef.current) return;
          logger.info('Call ended by remote user via SocketManager:', data);

          // Use ref to call latest version of handleEndCall
          if (handleEndCallRef.current) {
            handleEndCallRef.current();
          }
          addNotification('Call ended', 'info');
        },
        onCallRejected: (data) => {
          if (!mountedRef.current) return;
          logger.info('Call rejected by remote user via SocketManager:', data);
          
          // Use ref to call latest version of handleEndCall
          if (handleEndCallRef.current) {
            handleEndCallRef.current();
          }
          addNotification('Call was rejected', 'info');
        },
        onError: (error) => {
          if (!mountedRef.current) return;
          setCallError(error.message);
          addNotification(error.message, 'error');
          logger.error('Socket error:', error);
        }
      });

      // Initialize Message Manager
      messageManagerRef.current = new MessageManager({
        currentUser,
        receiverUserId,
        chatRoomId,
        logger,
        config: CONFIG.MESSAGE,
        onMessageReceived: (message) => {
          if (!mountedRef.current) return;
          
          // Critical Fix: Only show messages between current user and selected receiver
          const isConversationMessage = (
            (message.sender === currentUser?.socketId && message.receiver === receiverUserId) ||
            (message.sender === receiverUserId && message.receiver === currentUser?.socketId)
          );
          
          if (!isConversationMessage) {
            logger.info(`🚫 MessageManager message ${message.id} filtered out - not part of current conversation:`, {
              sender: message.sender,
              receiver: message.receiver,
              currentUserId: currentUser?.socketId,
              receiverUserId: receiverUserId
            });
            return;
          }
          
          // Only validate text for text messages, allow other types
          if (message.type === 'text' && !validateMessage(message.text)) {
            logger.warn('Invalid text message received:', message);
            return;
          }
          
          if (!processedMessageIds.current.has(message.id)) {
            processedMessageIds.current.add(message.id);
            setMessages(prev => {
              const processedMessage = {
                ...message,
                timestamp: new Date(message.timestamp)
              };
              
              // Only sanitize text for text messages
              if (message.type === 'text') {
                processedMessage.text = sanitizeInput(message.text);
              }
              
              const newMessages = [...prev, processedMessage];
              // Limit messages to prevent memory issues
              return newMessages.slice(-CONFIG.MESSAGE.HISTORY_LIMIT);
            });
          }
        },
        onMessageHistory: (messages) => {
          if (!mountedRef.current) return;
          
          const validMessages = messages
            .filter(msg => {
              if (!msg || !msg.id) return false;
              
              // Critical Fix: Only show messages between current user and selected receiver
              const isConversationMessage = (
                (msg.sender === currentUser?.socketId && msg.receiver === receiverUserId) ||
                (msg.sender === receiverUserId && msg.receiver === currentUser?.socketId)
              );
              
              if (!isConversationMessage) {
                logger.info(`🚫 Message ${msg.id} filtered out from MessageManager - not part of current conversation:`, {
                  sender: msg.sender,
                  receiver: msg.receiver,
                  currentUserId: currentUser?.socketId,
                  receiverUserId: receiverUserId
                });
                return false;
              }
              
              // Only validate text for text messages
              if (msg.type === 'text') {
                return validateMessage(msg.text);
              }
              // Allow all other message types (image, file, etc.)
              return true;
            })
            .map(msg => {
              const processedMessage = {
                ...msg,
                timestamp: new Date(msg.timestamp)
              };
              
              // Only sanitize text for text messages
              if (msg.type === 'text') {
                processedMessage.text = sanitizeInput(msg.text);
              }
              
              return processedMessage;
            })
            .slice(-CONFIG.MESSAGE.HISTORY_LIMIT);
            
          validMessages.forEach(msg => processedMessageIds.current.add(msg.id));
          setMessages(validMessages);
        },
        onError: (error) => {
          if (!mountedRef.current) return;
          setCallError(error.message);
          addNotification(error.message, 'error');
          logger.error('WebRTC error:', error);
        }
      });

      // Connect managers
      socketManagerRef.current.connect();

      // Signal that managers are ready – triggers incomingCallData auto-accept if needed
      if (mountedRef.current) {
        setManagersInitialized(true);
      }

      cleanup = () => {
        if (mountedRef.current) setManagersInitialized(false);
        try {
          webRTCManagerRef.current?.cleanup();
          // Use cleanup() (not disconnect()) so the shared socket from App.js stays connected.
          // disconnect() would force-close the shared socket, removing the user from the
          // server's activeUsers map and making them appear offline to other callers.
          socketManagerRef.current?.cleanup();
          messageManagerRef.current?.cleanup();
          performanceMonitorRef.current?.stop();
        } catch (error) {
          logger.error('Cleanup error:', error);
        }
      };
      
    } catch (error) {
      handleCriticalError(error, 'Manager Initialization');
    }

    return cleanup;
  }, [
    validationError, 
    currentUser, 
    receiverUserId, 
    chatRoomId, 
    socket, 
    logger, 
    addNotification,
    handleCriticalError,
    configLoading,
    webRTCConfig
  ]);

  // Auto-scroll to bottom with performance optimization
  useEffect(() => {
    if (messagesEndRef.current) {
      const scrollOptions = { 
        behavior: messages.length > 50 ? 'auto' : 'smooth' // Smooth scroll only for smaller lists
      };
      messagesEndRef.current.scrollIntoView(scrollOptions);
    }
  }, [messages]);

  // Call management functions with enhanced error handling
  const handleStartCall = useCallback(async (isVideo = false) => {
    if (!webRTCManagerRef.current || !socketManagerRef.current) {
      addNotification('Call system not ready', 'error');
      return;
    }

    try {
      setLoading(true);
      setCallType(isVideo ? 'video' : 'audio');
      setIsInCall(true);
      setCallStatus(CALL_STATES.CALLING);
      
      const callData = await webRTCManagerRef.current.startCall(isVideo, receiverUserId);
      setCurrentSessionId(callData.sessionId);
      
      // Send call offer via socket
      await socketManagerRef.current.sendCallOffer({
        callerId: currentUser.socketId,
        callerName: currentUser.name || 'Unknown',
        receiverId: receiverUserId,
        callType: isVideo ? 'video' : 'audio',
        sessionId: callData.sessionId,
        offer: callData.offer
      });
      
      logger.info('Call offer sent successfully');
      
    } catch (error) {
      logger.error('Failed to start call:', error);
      addNotification('Failed to start call: ' + error.message, 'error');
      setIsInCall(false);
      setCallStatus(CALL_STATES.IDLE);
    } finally {
      setLoading(false);
    }
  }, [receiverUserId, addNotification, logger]);

  const handleAcceptCall = useCallback(async () => {
    if (!incomingCall || !webRTCManagerRef.current || !socketManagerRef.current) return;

    try {
      // Show call interface immediately for better UX
      setCallType(incomingCall.callType);
      setIsInCall(true);
      setCallStatus(CALL_STATES.CONNECTING);
      setCurrentSessionId(incomingCall.sessionId);
      setIncomingCall(null);
      
      logger.info('Call acceptance started - UI updated immediately');
      
      // Accept the call in background
      const answerData = await webRTCManagerRef.current.acceptCall(incomingCall);
      
      // Send the answer back to the caller
      socketManagerRef.current.sendCallAnswer({
        ...answerData,
        callerId: incomingCall.callerId
      });
      
      logger.info('Call answer sent to caller');
      
    } catch (error) {
      logger.error('Failed to accept call:', error);
      addNotification('Failed to accept call: ' + error.message, 'error');
      setIncomingCall(null);
      setIsInCall(false);
      setCallStatus(CALL_STATES.IDLE);
    }
  }, [incomingCall, addNotification, logger]);

  const handleRejectCall = useCallback(() => {
    if (!incomingCall || !socketManagerRef.current) return;

    try {
      socketManagerRef.current.rejectCall(incomingCall.callerId, incomingCall.sessionId);
      setIncomingCall(null);
      addNotification('Call rejected', 'info');
    } catch (error) {
      logger.error('Failed to reject call:', error);
      addNotification('Failed to reject call', 'error');
    }
  }, [incomingCall, addNotification, logger]);

  const handleEndCall = useCallback(() => {
    try {
      logger.info('Ending call - starting cleanup process');
      
      // Stop WebRTC streams and connections
      webRTCManagerRef.current?.endCall();
      
      // Clear video elements immediately
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = null;
        logger.debug('Local video element cleared');
      }
      
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = null;
        logger.debug('Remote video element cleared');
      }
      
      // Notify server about call end
      if (socketManagerRef.current && currentSessionId) {
        socketManagerRef.current.endCall(receiverUserId, currentSessionId);
      }

      // Add to call history with proper validation
      const callRecord = {
        id: Date.now() + Math.random(),
        type: callType || 'audio',
        direction: 'outgoing',
        participant: sanitizeInput(receiver?.name || 'Unknown'),
        participantId: receiverUserId,
        duration: 0, // Calculate actual duration in production
        timestamp: new Date(),
        status: callStatus === CALL_STATES.CONNECTED ? 'completed' : 'missed'
      };
      
      setCallHistory(prev => [callRecord, ...prev.slice(0, CONFIG.UI.MAX_CALL_HISTORY - 1)]);

      // Reset all call-related state
      setIsInCall(false);
      setCallType(null);
      setCallStatus(CALL_STATES.IDLE);
      setCallError('');
      setCurrentSessionId(null);
      setIsMuted(false);
      setIsVideoOff(false);
      setIsScreenSharing(false);
      setIsSpeakerOn(false);

      logger.info('Call cleanup completed - all streams and UI cleared');

      // If this chat was opened from a global incoming-call notification,
      // close the whole chat interface so the user returns to their previous screen.
      // (When the user opened the chat manually, we leave it open as before.)
      if (incomingCallData && onClose) {
        onClose();
      }

    } catch (error) {
      logger.error('Error ending call:', error);
      addNotification('Error ending call', 'error');
      
      // Force cleanup even if errors occurred
      if (localVideoRef.current) localVideoRef.current.srcObject = null;
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
      setIsInCall(false);
      setCallType(null);
      setCallStatus(CALL_STATES.IDLE);
      setIsScreenSharing(false);
    }
  }, [
    webRTCManagerRef,
    socketManagerRef,
    currentSessionId,
    receiverUserId,
    callType,
    callStatus,
    receiver?.name,
    logger,
    addNotification,
    incomingCallData,
    onClose
  ]);

  // Update the ref whenever handleEndCall changes
  useEffect(() => {
    handleEndCallRef.current = handleEndCall;
  }, [handleEndCall]);

  // Media control functions with error handling
  const handleToggleMute = useCallback(() => {
    try {
      if (webRTCManagerRef.current) {
        const muted = webRTCManagerRef.current.toggleMute();
        setIsMuted(muted);
      }
    } catch (error) {
      logger.error('Failed to toggle mute:', error);
      addNotification('Failed to toggle mute', 'error');
    }
  }, [logger, addNotification]);

  const handleToggleVideo = useCallback(() => {
    try {
      if (webRTCManagerRef.current && callType === 'video') {
        const videoOff = webRTCManagerRef.current.toggleVideo();
        setIsVideoOff(videoOff);
      }
    } catch (error) {
      logger.error('Failed to toggle video:', error);
      addNotification('Failed to toggle video', 'error');
    }
  }, [callType, logger, addNotification]);

  const handleToggleSpeaker = useCallback(() => {
    try {
      if (webRTCManagerRef.current) {
        const speakerOn = webRTCManagerRef.current.toggleSpeaker();
        setIsSpeakerOn(speakerOn);
      }
    } catch (error) {
      logger.error('Failed to toggle speaker:', error);
      addNotification('Failed to toggle speaker', 'error');
    }
  }, [logger, addNotification]);

  const handleToggleScreenShare = useCallback(async () => {
    if (!webRTCManagerRef.current || callType !== 'video') return;

    try {
      if (isScreenSharing) {
        await webRTCManagerRef.current.stopScreenShare();
        setIsScreenSharing(false);
      } else {
        await webRTCManagerRef.current.startScreenShare();
        setIsScreenSharing(true);
      }
    } catch (error) {
      logger.error('Screen share error:', error);
      addNotification('Screen share failed: ' + error.message, 'error');
    }
  }, [callType, isScreenSharing, logger, addNotification]);

  // Enhanced message handling with validation and rate limiting
  const handleSendMessage = useCallback(() => {
    if (!newMessage.trim()) return;

    const trimmedMessage = newMessage.trim();
    
    // Validate message length
    if (trimmedMessage.length > CONFIG.MESSAGE.MAX_LENGTH) {
      addNotification(`Message too long (max ${CONFIG.MESSAGE.MAX_LENGTH} characters)`, 'error');
      return;
    }

    const sanitizedMessage = sanitizeInput(trimmedMessage);
    if (!validateMessage(sanitizedMessage)) {
      addNotification('Invalid message content', 'error');
      return;
    }

    try {
      const message = {
        id: Date.now() + Math.random(),
        text: sanitizedMessage,
        sender: currentUser.socketId,
        receiver: receiverUserId,
        timestamp: new Date().toISOString(),
        type: MESSAGE_TYPES.TEXT || 'text'
      };

      logger.info('🔥 SENDING MESSAGE DIRECTLY VIA SOCKET:', message);

      // Send directly via socket (Critical Fix)
      if (socket && socket.connected) {
        socket.emit('send-direct-message', message);
        logger.info('✅ Message sent via socket');

        // Add to local state immediately for better UX
        if (!processedMessageIds.current.has(message.id)) {
          processedMessageIds.current.add(message.id);
          setMessages(prev => {
            const newMessages = [...prev, {
              ...message,
              timestamp: new Date(message.timestamp)
            }];
            return newMessages.slice(-CONFIG.MESSAGE.HISTORY_LIMIT);
          });
        }
        setNewMessage('');
        
        // Notify parent component about the sent message
        if (onMessageSent) {
          onMessageSent(message);
        }
      } else {
        logger.error('❌ Socket not connected');
        addNotification('Connection lost - message not sent', 'error');
      }
    } catch (error) {
      logger.error('Error sending message:', error);
      addNotification('Error sending message', 'error');
    }
  }, [newMessage, currentUser.socketId, receiverUserId, addNotification, logger, socket, onMessageSent]);

  // Handle file message sending
  const handleSendFileMessage = useCallback((fileMessage) => {
    try {
      const message = {
        id: Date.now() + Math.random(),
        text: fileMessage.text || `Shared ${fileMessage.type}`,
        sender: currentUser.socketId,
        receiver: receiverUserId,
        timestamp: new Date(),
        type: fileMessage.type,
        fileName: fileMessage.fileName,
        url: fileMessage.url,
        fileSize: fileMessage.fileSize,
        mimeType: fileMessage.mimeType
      };

      logger.info('Sending file message', { type: message.type, fileName: message.fileName });

      // Try SocketManager first for file messages (more reliable)
      let sent = false;
      if (socketManagerRef.current?.sendMessage) {
        sent = socketManagerRef.current.sendMessage(message);
        logger.debug('File message sent via SocketManager', { sent, messageId: message.id });
      }

      // Fallback to MessageManager if socket failed
      if (!sent && messageManagerRef.current?.sendMessage) {
        sent = messageManagerRef.current.sendMessage(message);
        logger.debug('File message sent via MessageManager', { sent, messageId: message.id });
      }

      if (sent) {
        // Add to local state immediately for better UX
        if (!processedMessageIds.current.has(message.id)) {
          processedMessageIds.current.add(message.id);
          setMessages(prev => {
            const newMessages = [...prev, message];
            return newMessages.slice(-CONFIG.MESSAGE.HISTORY_LIMIT);
          });
        }
        addNotification(`${fileMessage.type} sent successfully`, 'success');
      } else {
        addNotification('Failed to send file', 'error');
        logger.error('Failed to send file message', { message });
      }
    } catch (error) {
      logger.error('Error sending file message:', error);
      addNotification('Error sending file', 'error');
    }
  }, [currentUser.socketId, receiverUserId, addNotification, logger]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  // Connection retry with exponential backoff
  const handleRetryConnection = useCallback(() => {
    if (retryCount >= CONFIG.SOCKET.RECONNECTION_ATTEMPTS) {
      addNotification('Maximum retry attempts reached', 'error');
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, retryCount), CONFIG.SOCKET.RECONNECTION_DELAY_MAX);
    setRetryCount(prev => prev + 1);
    setConnectionError(null);
    
    setTimeout(() => {
      socketManagerRef.current?.reconnect();
    }, delay);
  }, [retryCount, addNotification]);

  // Auto-accept a call that was already accepted in GlobalCallManager.
  // Runs only once managersInitialized becomes true so WebRTCManager and SocketManager
  // are guaranteed to exist. Using refs directly avoids stale-closure issues that
  // arise when going through the incomingCall state + handleAcceptCall callback.
  useEffect(() => {
    if (!incomingCallData || !managersInitialized) return;
    if (!webRTCManagerRef.current || !socketManagerRef.current || !mountedRef.current) return;

    logger.info('Auto-accepting call from GlobalCallManager:', incomingCallData);

    const callData = {
      callerId: incomingCallData.callerId,
      callerName: incomingCallData.callerName || 'Unknown',
      callType: incomingCallData.callType || 'audio',
      sessionId: incomingCallData.sessionId,
      offer: incomingCallData.offer
    };

    // Update UI state immediately so the call screen shows
    setCallType(callData.callType);
    setIsInCall(true);
    setCallStatus(CALL_STATES.CONNECTING);
    setCurrentSessionId(callData.sessionId);

    // Run WebRTC accept directly – don't go through the incomingCall state + handleAcceptCall
    // to avoid the stale-closure problem (handleAcceptCall reads incomingCall from its closure
    // which would still be null at this point).
    webRTCManagerRef.current.acceptCall(callData)
      .then(async answerData => {
        if (!mountedRef.current) return;
        socketManagerRef.current.sendCallAnswer({
          ...answerData,
          callerId: callData.callerId
        });
        logger.info('Auto-accept: call answer sent to caller');

        // Replay ICE candidates that arrived before the manager was ready
        const buffered = earlyIceCandidatesRef.current.splice(0);
        if (buffered.length > 0) {
          logger.info('Auto-accept: replaying', buffered.length, 'buffered ICE candidates');
          for (const candidateData of buffered) {
            await webRTCManagerRef.current.handleIceCandidate(candidateData);
          }
        }
      })
      .catch(error => {
        if (!mountedRef.current) return;
        logger.error('Auto-accept: failed to accept call:', error);
        addNotification('Failed to accept call: ' + error.message, 'error');
        setIsInCall(false);
        setCallStatus(CALL_STATES.IDLE);
      });
  }, [incomingCallData, managersInitialized, logger, addNotification]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Early return for validation errors
  if (validationError) {
    return (
      <ErrorBoundary>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
          padding: '20px'
        }}>
          <ErrorIcon style={{ fontSize: '48px', color: '#ff4444', marginBottom: '16px' }} />
          <div style={{ color: '#666', fontSize: '16px', marginBottom: '16px', textAlign: 'center' }}>
            {validationError}
          </div>
          <Button variant="contained" onClick={onClose} color="primary">
            Close
          </Button>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        width: '100%',
        fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
        position: 'relative',
        backgroundColor: '#fff',
        overflow: 'hidden'
      }}>
        
        {/* Loading Overlay */}
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9998
          }}>
            <CircularProgress style={{ color: '#25D366' }} />
          </div>
        )}

        {/* Critical Error Dialog */}
        <Dialog 
          open={showErrorDialog} 
          onClose={() => setShowErrorDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Warning style={{ color: '#ff4444', marginRight: '8px' }} />
            System Error
          </DialogTitle>
          <DialogContent>
            <div style={{ marginBottom: '16px' }}>
              A critical error occurred in the chat system. Please try refreshing the page.
            </div>
            {criticalError && (
              <div style={{ 
                backgroundColor: '#f5f5f5', 
                padding: '12px', 
                borderRadius: '4px',
                fontSize: '12px',
                fontFamily: 'monospace'
              }}>
                {criticalError.context}: {criticalError.error.message}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowErrorDialog(false)}>
              Dismiss
            </Button>
            <Button 
              onClick={() => window.location.reload()} 
              variant="contained" 
              color="primary"
            >
              Refresh Page
            </Button>
          </DialogActions>
        </Dialog>

        {/* Incoming Call Dialog - Only show if not handled by GlobalCallManager */}
        {incomingCall && !incomingCallData && (
          <IncomingCallDialog
            incomingCall={incomingCall}
            onAccept={handleAcceptCall}
            onReject={handleRejectCall}
          />
        )}

        {/* Chat Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          backgroundColor: '#128C7E',
          color: 'white',
          borderRadius: '8px 8px 0 0',
          minHeight: '64px'
        }}>
          <IconButton 
            onClick={onClose} 
            style={{ color: 'white', marginRight: '8px' }}
            disabled={loading}
            aria-label="Close chat"
          >
            <ArrowBack />
          </IconButton>
          
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: '#25D366',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px',
            fontSize: '16px',
            fontWeight: 'bold'
          }}>
            {receiver?.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '500', fontSize: '16px' }}>
              {sanitizeInput(receiver?.name || 'Unknown User')}
            </div>
            <div style={{ fontSize: '13px', opacity: 0.8 }}>
              {connectionError ? (
                <span style={{ color: '#ffcccc' }}>
                  Connection Error
                  <IconButton 
                    size="small" 
                    onClick={handleRetryConnection}
                    style={{ color: 'white', marginLeft: '4px' }}
                    disabled={retryCount >= CONFIG.SOCKET.RECONNECTION_ATTEMPTS}
                    aria-label="Retry connection"
                  >
                    <Refresh fontSize="small" />
                  </IconButton>
                </span>
              ) : isConnected ? (isOnline ? 'Online' : 'Offline') : 'Connecting...'}
              {isInCall && <span> • {callType?.toUpperCase()} Call Active</span>}
            </div>
          </div>
          
          {/* Call Controls */}
          {!isInCall ? (
            <>
              <IconButton
                style={{ color: 'white', marginLeft: '8px' }}
                onClick={() => handleStartCall(false)}
                title="Start Audio Call"
                disabled={!isConnected || loading}
                aria-label="Start audio call"
              >
                <Phone />
              </IconButton>
              <IconButton
                style={{ color: 'white', marginLeft: '4px' }}
                onClick={() => handleStartCall(true)}
                title="Start Video Call"
                disabled={!isConnected || loading}
                aria-label="Start video call"
              >
                <VideoCall />
              </IconButton>
            </>
          ) : (
            <IconButton 
              style={{ color: '#ff4444', marginLeft: '8px' }}
              onClick={handleEndCall}
              title="End Call"
              aria-label="End call"
            >
              <CallEnd />
            </IconButton>
          )}
          
          <IconButton 
            style={{ color: 'white', marginLeft: '4px' }}
            aria-label="More options"
          >
            <MoreVert />
          </IconButton>
        </div>


        {/* Messages Area - Hidden during call */}
        {!isInCall && (
          <MessageList
            messages={messages}
            currentUserId={currentUser.socketId}
            messagesEndRef={messagesEndRef}
            isCallActive={isInCall}
            currentUser={currentUser}
            receiver={receiver}
          />
        )}

        {/* Message Input - Hidden during call */}
        {!isInCall && (
          <MessageInput
            message={newMessage}
            onMessageChange={setNewMessage}
            onSendMessage={handleSendMessage}
            onKeyPress={handleKeyPress}
            showEmojiPicker={showEmojiPicker}
            onToggleEmojiPicker={() => setShowEmojiPicker(!showEmojiPicker)}
            disabled={!isConnected || loading}
            hasDataChannel={messageManagerRef.current?.hasDataChannel()}
            maxLength={CONFIG.MESSAGE.MAX_LENGTH}
            onSendFileMessage={handleSendFileMessage}
          />
        )}

        {/* Notifications */}
        {notifications.map((notification) => (
          <Snackbar
            key={notification.id}
            open={true}
            autoHideDuration={notification.duration}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            onClose={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
          >
            <Alert severity={notification.type} variant="filled">
              {notification.message}
            </Alert>
          </Snackbar>
        ))}
      </div>

      {/* Full Screen Call Interface using Portal */}
      {isInCall && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 10001,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.95)'
        }}>
          <CallInterface
            callType={callType}
            callStatus={callStatus}
            callError={callError}
            isMuted={isMuted}
            isVideoOff={isVideoOff}
            isSpeakerOn={isSpeakerOn}
            isScreenSharing={isScreenSharing}
            localVideoRef={localVideoRef}
            remoteVideoRef={remoteVideoRef}
            onToggleMute={handleToggleMute}
            onToggleVideo={handleToggleVideo}
            onToggleSpeaker={handleToggleSpeaker}
            onToggleScreenShare={handleToggleScreenShare}
            onEndCall={handleEndCall}
          />
        </div>,
        document.body
      )}
    </ErrorBoundary>
  );
};

export default React.memo(ChatAndCall);
